import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { remarkRefPlugin } from './remark-ref-plugin';

export interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * SA-15 / SEC-006 — explicit link protocol allow-list.
 *
 * Markdown content can carry attacker-influenced URLs (LLM output, imported
 * docs, marketplace content). Only `http:`, `https:` and `mailto:` links are
 * rendered; `javascript:`, `data:`, `vbscript:` and any other scheme are
 * neutralized (href omitted) so a crafted link cannot execute script.
 */
export const SAFE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'] as const;

export function sanitizeLinkHref(href: string | undefined): string | undefined {
  if (typeof href !== 'string') return undefined;
  const value = href.trim();
  if (!value) return undefined;

  // In-page anchors are safe and must keep their relative form.
  if (value.startsWith('#')) return value;

  const base =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://app.local';

  let url: URL;
  try {
    url = new URL(value, base);
  } catch {
    return undefined;
  }

  if (!(SAFE_LINK_PROTOCOLS as readonly string[]).includes(url.protocol)) {
    return undefined;
  }
  return url.href;
}

/**
 * Custom component overrides that apply Tailwind utility classes
 * for markdown rendering (no @tailwindcss/typography dependency needed).
 */
const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-2xl font-bold mt-4 mb-2 text-foreground">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-semibold mt-3 mb-2 text-foreground">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-semibold mt-3 mb-1 text-foreground">{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-semibold mt-2 mb-1 text-foreground">{children}</h4>,
  p: ({ children }) => <p className="my-1.5 leading-relaxed text-foreground">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5 my-1.5 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-foreground">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-muted-foreground/30 pl-3 my-2 text-muted-foreground italic">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <pre className="bg-muted rounded-md p-3 my-2 overflow-x-auto">
          <code className={`text-sm font-mono text-foreground ${className ?? ''}`}>{children}</code>
        </pre>
      );
    }
    return (
      <code className="bg-muted rounded px-1.5 py-0.5 text-sm font-mono text-foreground">{children}</code>
    );
  },
  pre: ({ children }) => <>{children}</>,
  a: ({ href, children }) => {
    const safeHref = sanitizeLinkHref(href);
    return (
      <a
        href={safeHref}
        className="text-primary underline underline-offset-2 hover:text-primary/80"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  },
  hr: () => <hr className="my-3 border-border" />,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b-2 border-border">{children}</thead>,
  th: ({ children }) => <th className="text-left p-2 font-semibold text-foreground">{children}</th>,
  td: ({ children }) => <td className="p-2 border-b border-border text-foreground">{children}</td>,
  span: ({ className, children, ...props }) => {
    if (className === 'ref-badge') {
      return (
        <span
          className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded px-1.5 py-0.5 text-xs font-mono"
          {...props}
        >
          {children}
        </span>
      );
    }
    return <span className={className} {...props}>{children}</span>;
  },
};

/**
 * Shared markdown renderer with @ref() badge support.
 * Uses react-markdown + remark-gfm + custom remark-ref-plugin.
 * Applies Tailwind utility classes via component overrides.
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className }) => {
  return (
    <div className={`max-w-none text-sm ${className ?? ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkRefPlugin]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
